import React from 'react';
import PropTypes from 'prop-types';
import { BackendError, CrmError } from '../../fetchGraphQL';
import classNames from 'classnames';
import styles from './LocalErrorBoundary.module.scss';
import { ErrorIcon } from '../Icon/Icon';
import { useTranslation } from 'react-i18next';

const decorateWithHooks = (Component) => {
    return (props) => {
        const { t } = useTranslation();
        return <Component t={t} {...props} />;
    };
};

class BackendErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, crmError: false, code: null };
    }

    static getDerivedStateFromError(error) {
        if (error instanceof CrmError) {
            return { hasError: true, error, crmError: true, code: error.code };
        }
        if (error instanceof BackendError) {
            return { hasError: true, error, crmError: false, code: null };
        }
        throw error;
    }

    componentDidCatch(error, info) {
        console.error('BackendErrorBoundary caught error:', error, info?.componentStack);
        this.props.onError?.(error, info);
    }

    renderCrmError() {
        const { t } = this.props;
        const { code } = this.state;

        if (code === 'ANSPRECHPARTNER_NOT_FOUND') {
            return (
                <div
                    role='alert'
                    aria-live='assertive'
                    className={classNames(
                        styles.LocalErrorBoundary,
                        styles.LocalErrorBoundaryMultiline
                    )}
                >
                    <ErrorIcon className={styles.ErrorIcon} aria-hidden='true' />
                    <span className={styles.ErrorHeadline}>
                        {t('errors.crmErrors.ansprechpartnerNotFound.title')}
                    </span>
                    <p className={styles.ErrorMessage}>
                        {t('errors.crmErrors.ansprechpartnerNotFound.message.1')}
                    </p>
                    <p className={styles.ErrorMessage}>
                        {t('errors.crmErrors.ansprechpartnerNotFound.message.2')}
                    </p>
                </div>
            );
        }

        // Unknown CRM code: generic fallback
        return this.renderGenericError();
    }

    renderGenericError() {
        const { t } = this.props;
        const { error } = this.state;

        const message =
            error instanceof BackendError && error.message
                ? error.message
                : t('generics.unexpectedError');

        return (
            <div
                role='alert'
                aria-live='assertive'
                className={classNames(
                    styles.LocalErrorBoundary,
                    styles.LocalErrorBoundaryMultiline
                )}
            >
                <ErrorIcon className={styles.ErrorIcon} aria-hidden='true' />
                <span className={styles.ErrorMessage}>{message}</span>
            </div>
        );
    }

    render() {
        const { hasError, crmError } = this.state;
        if (hasError) {
            return crmError ? this.renderCrmError() : this.renderGenericError();
        }
        return this.props.children;
    }
}

BackendErrorBoundary.propTypes = {
    children: PropTypes.node,
    t: PropTypes.func.isRequired,
    onError: PropTypes.func,
};

export default decorateWithHooks(BackendErrorBoundary);
